import React, {useCallback, useEffect, useMemo, useState} from 'react';
import Modal from '../common/Modal';
import {characterService} from '../../services/characterService';
import {
  AttributeEnum,
  CharacterAppearanceDTO,
  CharacterCreationConfigDTO,
  CharacterCreationStepEnum,
  getAttributeModifier,
  getCombatMovement,
  getMaxHP,
  getMaxMP,
  MotivationDTO,
  PlayerCharacterDTO,
  Pronouns,
  SkillEnum
} from '@hexcrawl/shared-utils';


function formatModifier(value: number): string {
  const mod = getAttributeModifier(value);
  return mod >= 0 ? `+${mod}` : `${mod}`;
}
import {LoadingKeys, useUiStore} from "@/ui/stores/uiStore.ts";
import {AppearanceStep} from './AppearanceStep';
import {ATTRIBUTE_ICONS, ATTRIBUTE_LABELS, DERIVED_STAT_ICONS, DERIVED_STAT_LABELS, SKILL_ICONS, SKILL_LABELS} from '../../constants/entityDisplay';

interface Props {
  initialCharacter?: PlayerCharacterDTO;
  onComplete: () => void;
}

export const CharacterCreation: React.FC<Props> = ({initialCharacter, onComplete}) => {
  const [character, setCharacter] = useState<PlayerCharacterDTO | null>(initialCharacter ?? null);
  const [config, setConfig] = useState<CharacterCreationConfigDTO | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [characterName, setCharacterName] = useState('');
  const isLoading = useUiStore((s) => s.isLoading);
  const startLoading = useUiStore((s) => s.startLoading);
  const stopLoading = useUiStore((s) => s.stopLoading);
  const setError = useUiStore((s) => s.setError);
  const errorMessage = useUiStore((s) => s.errorMessage);

  // local UI state
  const [introSeen, setIntroSeen] = useState(false);
  const [shuffledAnswers, setShuffledAnswers] = useState<string[][]>([]);
  const [answerIndices, setAnswerIndices] = useState<number[]>([]);
  const [pronouns, setPronouns] = useState<Pronouns>(Pronouns.THEY_THEM);

  const shuffleArray = useCallback(<T,>(arr: T[]): T[] => {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }, []);

  // Load config once
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cfg = await characterService.config();
        if (!mounted) return;
        setConfig(cfg);
        const shuffled = cfg.questions.map(q => shuffleArray(q.defaultAnswers));
        setShuffledAnswers(shuffled);
        setAnswerIndices(cfg.questions.map(() => 0));
      } catch (e: any) {
        if (mounted) setError(e?.message || 'Failed to load character config');
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // If no initialCharacter provided, create a new character on mount (once config is loaded)
  useEffect(() => {
    if (initialCharacter) return; // already have one
    if (!config) return; // wait for config
    if (character) return; // already created

    let mounted = true;
    (async () => {
      startLoading(LoadingKeys.CHARACTER, 'Preparing character…');
      try {
        const response = await characterService.create();
        if (!mounted) return;
        if (response.character) {
          const motivations = config.questions.map((q, idx) => {
            const existing = response.character!.motivations?.[idx];
            return existing ? {...existing, answer: existing.answer ?? ''} : {question: q.question, answer: ''};
          });
          setCharacter({...response.character, motivations});
        }
      } catch (e: any) {
        if (mounted) setError(e?.message || 'Failed to create character');
      } finally {
        if (mounted) stopLoading(LoadingKeys.CHARACTER);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [config]);

  // When we have both config and an initialCharacter, prime motivations
  useEffect(() => {
    if (!config) return;
    if (!initialCharacter) return;

    const motivations = config.questions.map((q, idx) => {
      const existing = initialCharacter.motivations?.[idx];
      if (existing) {
        return {...existing, answer: existing.answer ?? ''};
      }
      return {question: q.question, answer: ''};
    });
    setCharacter({...initialCharacter, motivations});
    stopLoading(LoadingKeys.CHARACTER);
  }, [config]);

  // Pre-populate motivation answers with suggestions when entering the QUESTIONS step
  useEffect(() => {
    if (!character || !shuffledAnswers.length) return;
    if (character.creationStep !== CharacterCreationStepEnum.QUESTIONS) return;
    const needsPreFill = character.motivations.some((m, idx) =>
      !m.answer?.trim() && shuffledAnswers[idx]?.[answerIndices[idx]]
    );
    if (!needsPreFill) return;
    const nextMotivations = character.motivations.map((m, idx) => ({
      ...m,
      answer: m.answer?.trim() ? m.answer : (shuffledAnswers[idx]?.[answerIndices[idx]] ?? ''),
    }));
    setCharacter({...character, motivations: nextMotivations});
  }, [character?.creationStep, shuffledAnswers]);

  // Pools for attributes/skills
  const attrPool = useMemo(() => {
    if (!character || !config) return 0;
    const total = Object.values(character.attributes).reduce((a, b) => a + b, 0);
    return config.attributes.points - total;
  }, [character, config]);

  const skillsPool = useMemo(() => {
    if (!character || !config) return 0;
    const total = Object.values(character.skills).reduce((a, b) => a + b, 0);
    return config.skills.points - total;
  }, [character, config]);

  const incAttr = (key: AttributeEnum, delta: 1 | -1) => {
    if (!character || !config) return;
    if (delta > attrPool) return;

    const prevValue = character.attributes[key] || 0;
    character.attributes[key] = Math.min(config.attributes.max, Math.max(config.attributes.min, prevValue + delta));
    setCharacter({...character});
  };

  const incSkill = (key: SkillEnum, delta: 1 | -1) => {
    if (!character || !character.skills || !config) return;
    if (delta > skillsPool) return;

    const prevValue = character.skills[key] || 0;
    const nextVal = Math.min(config.skills.max, Math.max(config.skills.min, prevValue + delta));
    const nextSkills = {...character.skills};
    if (nextVal === 0) {
      delete nextSkills[key];
    } else {
      nextSkills[key] = nextVal;
    }
    setCharacter({...character, skills: nextSkills});
  };

  // Actions
  const submitMotivations = async () => {
    if (!character || submitting) return;
    const motivations: MotivationDTO[] = [];
    character.motivations.forEach((m, idx) => {
      const answer = m.answer && m.answer.trim().length > 0
        ? m.answer
        : (shuffledAnswers[idx]?.[answerIndices[idx]] ?? '');
      motivations.push({question: m.question, answer});
    });
    setSubmitting(true);
    startLoading(LoadingKeys.CHARACTER, 'Saving...');
    try {
      const response = await characterService.update({id: character.id, motivations});
      if (response.character) {
        setCharacter(response.character);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to submit motivations');
    } finally {
      setSubmitting(false);
      stopLoading(LoadingKeys.CHARACTER);
    }
  };

  const submitAppearance = async (appearance: CharacterAppearanceDTO) => {
    if (!character || submitting) return;
    setSubmitting(true);
    startLoading(LoadingKeys.CHARACTER, 'Saving...');
    try {
      const response = await characterService.update({id: character.id, appearance});
      if (response.character) setCharacter(response.character);
    } catch (e: any) {
      setError(e?.message || 'Failed to save appearance');
    } finally {
      setSubmitting(false);
      stopLoading(LoadingKeys.CHARACTER);
    }
  };

  const submitAttributes = async () => {
    if (!character || submitting) return;
    if (attrPool !== 0) return;
    setSubmitting(true);
    startLoading(LoadingKeys.CHARACTER, 'Saving...');
    try {
      const response = await characterService.update({id: character.id, attributes: character.attributes});
      if (response.character) {
        setCharacter(response.character);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to submit attributes');
    } finally {
      setSubmitting(false);
      stopLoading(LoadingKeys.CHARACTER);
    }
  };

  const submitSkills = async () => {
    if (!character || submitting) return;
    if (skillsPool !== 0) return;
    setSubmitting(true);
    startLoading(LoadingKeys.CHARACTER, 'Saving...');
    try {
      const response = await characterService.update({id: character.id, skills: character.skills});
      if (response.character) {
        setCharacter(response.character);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to submit skills');
    } finally {
      setSubmitting(false);
      stopLoading(LoadingKeys.CHARACTER);
    }
  };

  const submitName = async () => {
    if (!character || submitting) return;
    const trimmed = characterName.trim();
    if (trimmed.length < 2) return;
    setSubmitting(true);
    startLoading(LoadingKeys.CHARACTER, 'Saving...');
    try {
      const response = await characterService.update({id: character.id, name: trimmed, pronouns});
      if (response.character) {
        setCharacter(response.character);
        onComplete();
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to save name');
    } finally {
      setSubmitting(false);
      stopLoading(LoadingKeys.CHARACTER);
    }
  };

  const attributeValue = (attr: AttributeEnum): number => {
    return character ? character.attributes[attr] || 0 : 0;
  };

  const skillValue = (skill: SkillEnum): number => {
    return character ? character.skills[skill] || 0 : 0;
  };

  const isOpen = Boolean(character && character.creationStep !== CharacterCreationStepEnum.COMPLETE);
  const showIntro = isOpen && character?.creationStep === CharacterCreationStepEnum.QUESTIONS && !introSeen;

  if (!isOpen && !isLoading) return null;

  return (
    <Modal isOpen={true} onClose={undefined}>
      {!isLoading && !errorMessage && character && config && (
        <div style={{
          width: '100%',
          maxWidth: character.creationStep === CharacterCreationStepEnum.APPEARANCE ? 800 : 600,
          boxSizing: 'border-box',
        }}>
          {/* Wizard header */}
          <div style={{marginBottom: 12}}>
            <div style={{fontSize: 18, fontWeight: 'bold'}}>Character Creation</div>
            <div style={{fontSize: 12, opacity: 0.75}}>Step: {showIntro ? 'Introduction' : character.creationStep}</div>
          </div>

          {/* Intro Step */}
          {showIntro && (
            <div>
              <p>Welcome to the adventure! Before you set out, we'll learn what drives your character and tune their strengths.</p>
              <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 12}}>
                <button style={mergeBtn(false, submitting)} disabled={submitting} onClick={() => setIntroSeen(true)}>Next</button>
              </div>
            </div>
          )}

          {/* Questions Step */}
          {!showIntro && character.creationStep === CharacterCreationStepEnum.QUESTIONS && (
            <div>
              <div style={{marginBottom: 16}}>Answer the three questions to define your character's motivations.</div>
              {config.questions.map((entry, idx) => {
                return (
                  <div key={idx} style={{marginBottom: 12}}>
                    <div style={{marginBottom: 4}}>{entry.question}</div>
                    <div style={{display: 'flex', gap: 6, alignItems: 'flex-start'}}>
                      <textarea
                        value={character.motivations[idx].answer ?? ''}
                        onChange={(e) => {
                          const next = {...character};
                          const nextMotivations = next.motivations.slice();
                          const current = nextMotivations[idx] || {};
                          nextMotivations[idx] = {
                            ...current,
                            answer: e.target.value.slice(0, 240),
                          };
                          next.motivations = nextMotivations;
                          setCharacter(next);
                        }}
                        rows={3}
                        disabled={submitting}
                        style={{flex: 1, resize: 'vertical', opacity: submitting ? 0.7 : 1}}
                      />
                      <button
                        title="Show another example"
                        disabled={submitting}
                        onClick={() => {
                          setAnswerIndices(prev => {
                            const next = [...prev];
                            const list = shuffledAnswers[idx];
                            next[idx] = list ? (next[idx] + 1) % list.length : 0;
                            const newSuggestion = list?.[next[idx]] ?? '';
                            setCharacter(prev => {
                              if (!prev) return prev;
                              const nextMotivations = prev.motivations.slice();
                              nextMotivations[idx] = {...nextMotivations[idx], answer: newSuggestion};
                              return {...prev, motivations: nextMotivations};
                            });
                            return next;
                          });
                        }}
                        style={{
                          padding: '4px 7px',
                          borderRadius: 4,
                          border: '1px solid #555',
                          background: '#1a1a2e',
                          color: '#aaa',
                          cursor: submitting ? 'not-allowed' : 'pointer',
                          fontSize: 14,
                          lineHeight: 1,
                          flexShrink: 0,
                          marginTop: 2,
                        }}
                      >↻</button>
                    </div>
                  </div>
                );
              })}
              <div style={{display: 'flex', gap: 8, alignItems: 'center'}}>
                <span style={{fontSize: 12, opacity: 0.7}}>Answers: {Math.min(3, character.motivations.filter(m => m.answer && m.answer.trim().length > 0).length)} / {Math.min(3, config.questions.length)}</span>
                <div style={{flex: 1}}/>
                <button style={mergeBtn(false, submitting)} disabled={submitting} onClick={submitMotivations}>
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Appearance Step */}
          {character.creationStep === CharacterCreationStepEnum.APPEARANCE && (
            <AppearanceStep submitting={submitting} onSubmit={submitAppearance} />
          )}

          {/* Attributes Step */}
          {character.creationStep === CharacterCreationStepEnum.ATTRIBUTES && (
            <div>
              {/* Header */}
              <div style={{marginBottom: 12}}>
                <div style={{marginBottom: 4}}>Distribute your ability points.</div>
                <div style={{display: 'flex', gap: 16, alignItems: 'center'}}>
                  <span style={{fontSize: 13}}>
                    Points remaining: <strong style={{color: attrPool === 0 ? '#88cc88' : '#e8c84a'}}>{attrPool}</strong>
                  </span>
                  <span style={{fontSize: 11, opacity: 0.55}}>Min {config.attributes.min} · Max {config.attributes.max}</span>
                </div>
              </div>

              {/* Attribute rows */}
              <div style={{marginBottom: 14}}>
                {Object.values(AttributeEnum).map((key) => {
                  const val = attributeValue(key);
                  const mod = getAttributeModifier(val);
                  const canDec = val > config.attributes.min && !submitting;
                  const canInc = attrPool > 0 && val < config.attributes.max && !submitting;
                  return (
                    <div key={key} style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8}}>
                      {/* Icon + name */}
                      <span style={{fontSize: 18, width: 24, textAlign: 'center', lineHeight: 1}}>{ATTRIBUTE_ICONS[key]}</span>
                      <div style={{width: 80, fontSize: 13}}>{ATTRIBUTE_LABELS[key]}</div>
                      {/* Stepper */}
                      <div style={{display: 'flex', gap: 4, alignItems: 'center'}}>
                        <button style={mergeBtn(!canDec, false)} onClick={() => incAttr(key, -1)} disabled={!canDec}>−</button>
                        <div style={{minWidth: 26, textAlign: 'center', fontSize: 15, fontWeight: 'bold'}}>{val}</div>
                        <button style={mergeBtn(!canInc, false)} onClick={() => incAttr(key, +1 as 1)} disabled={!canInc}>+</button>
                      </div>
                      {/* Modifier */}
                      <div style={{
                        minWidth: 32, textAlign: 'center', fontSize: 12, fontWeight: 'bold',
                        color: mod > 0 ? '#88cc88' : mod < 0 ? '#cc8888' : '#888',
                      }}>
                        {formatModifier(val)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Derived stats panel */}
              <div style={{
                background: '#0d0d1a',
                border: '1px solid #334',
                borderRadius: 6,
                padding: '10px 14px',
                marginBottom: 14,
                display: 'flex',
                gap: 24,
              }}>
                <div style={derivedStatStyle}>
                  <span style={{fontSize: 16}}>{DERIVED_STAT_ICONS.maxHp}</span>
                  <span style={{fontSize: 11, color: '#aaa'}}>{DERIVED_STAT_LABELS.maxHp}</span>
                  <span style={{fontSize: 16, fontWeight: 'bold', color: '#e88'}}>{getMaxHP(character)}</span>
                </div>
                <div style={derivedStatStyle}>
                  <span style={{fontSize: 16}}>{DERIVED_STAT_ICONS.maxMp}</span>
                  <span style={{fontSize: 11, color: '#aaa'}}>{DERIVED_STAT_LABELS.maxMp}</span>
                  <span style={{fontSize: 16, fontWeight: 'bold', color: '#88c'}}>{getMaxMP(character)}</span>
                </div>
                <div style={derivedStatStyle}>
                  <span style={{fontSize: 16}}>{DERIVED_STAT_ICONS.combatMove}</span>
                  <span style={{fontSize: 11, color: '#aaa'}}>{DERIVED_STAT_LABELS.combatMove}</span>
                  <span style={{fontSize: 16, fontWeight: 'bold', color: '#8c8'}}>{getCombatMovement(character)}</span>
                </div>
              </div>

              <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                <button style={mergeBtn(attrPool !== 0 || submitting, false)} disabled={attrPool !== 0 || submitting} onClick={submitAttributes}>Next</button>
              </div>
            </div>
          )}

          {/* Skills Step */}
          {character.creationStep === CharacterCreationStepEnum.SKILLS && (
            <div>
              <div style={{marginBottom: 8}}>
                <div>Distribute your skill points.</div>
                <div>Remaining: <strong>{skillsPool}</strong></div>
                <div style={{fontSize: 12, opacity: 0.7}}>Min {config.skills.min} / Max {config.skills.max}</div>
              </div>
              {Object.values(SkillEnum).map((key) => {
                const val = skillValue(key);
                const canDec = val > config.skills.min && !submitting;
                const canInc = skillsPool > 0 && val < config.skills.max && !submitting;
                return (
                  <div key={key} style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8}}>
                    <span style={{fontSize: 18, width: 24, textAlign: 'center', lineHeight: 1}}>{SKILL_ICONS[key]}</span>
                    <div style={{width: 90, fontSize: 13}}>{SKILL_LABELS[key]}</div>
                    <div style={{display: 'flex', gap: 4, alignItems: 'center'}}>
                      <button style={mergeBtn(!canDec, false)} onClick={() => incSkill(key, -1)} disabled={!canDec}>−</button>
                      <div style={{minWidth: 26, textAlign: 'center', fontSize: 15, fontWeight: 'bold'}}>{val}</div>
                      <button style={mergeBtn(!canInc, false)} onClick={() => incSkill(key, +1 as 1)} disabled={!canInc}>+</button>
                    </div>
                  </div>
                );
              })}
              <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 10}}>
                <button style={mergeBtn(skillsPool !== 0 || submitting, false)} disabled={skillsPool !== 0 || submitting} onClick={submitSkills}>Next</button>
              </div>
            </div>
          )}

          {/* Name Step */}
          {character.creationStep === CharacterCreationStepEnum.NAME && (
            <div>
              <div style={{marginBottom: 12}}>
                What is your character's name?
              </div>
              <input
                type="text"
                value={characterName}
                onChange={(e) => setCharacterName(e.target.value.slice(0, 32))}
                placeholder="Enter a name…"
                disabled={submitting}
                autoFocus
                style={{
                  width: '100%',
                  padding: '8px',
                  marginBottom: 4,
                  boxSizing: 'border-box',
                  background: '#1a1a2e',
                  color: 'white',
                  border: '1px solid #555',
                  borderRadius: 4,
                }}
              />
              <div style={{fontSize: 12, opacity: 0.7, marginBottom: 16}}>
                {characterName.trim().length} / 32 characters (minimum 2)
              </div>
              <div style={{marginBottom: 8}}>
                What are your character's pronouns?
              </div>
              <div style={{display: 'flex', gap: 8, marginBottom: 16}}>
                {(Object.values(Pronouns) as Pronouns[]).map((opt) => (
                  <button
                    key={opt}
                    disabled={submitting}
                    onClick={() => setPronouns(opt)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 4,
                      border: pronouns === opt ? '2px solid #4CAF50' : '2px solid #555',
                      background: pronouns === opt ? '#1e3a1e' : '#1a1a2e',
                      color: pronouns === opt ? '#4CAF50' : '#aaa',
                      cursor: submitting ? 'default' : 'pointer',
                      fontWeight: pronouns === opt ? 'bold' : 'normal',
                    }}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                <button
                  style={mergeBtn(characterName.trim().length < 2 || submitting, false)}
                  disabled={characterName.trim().length < 2 || submitting}
                  onClick={submitName}
                >
                  Finish
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

const btnStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 4,
  border: 'none',
  background: '#4CAF50',
  color: 'white',
  cursor: 'pointer',
};

const mergeBtn = (disabled: boolean, submitting: boolean): React.CSSProperties => ({
  ...btnStyle,
  background: disabled || submitting ? '#777' : btnStyle.background,
  cursor: disabled || submitting ? 'not-allowed' : 'pointer',
  opacity: disabled || submitting ? 0.6 : 1,
});

const derivedStatStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
};
